"use client";

// The Body Ballancer hub.
//
// This page used to carry five deep-dive sections inline — post-operative
// recovery, injury prevention, the liver–lymph connection, cellulite and skin,
// and the glymphatic system — roughly 2,000 words where each visitor wanted one
// and scrolled past four. They now live at /treatments/body-ballancer/<pathway>
// (app/data/ballancerPathways.ts), so each has its own title tag and its own
// search surface. The hub keeps the shared case for the treatment and the
// booking flow; the pathway cards below are the doors into the detail.

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import TrackedLink from "@components/TrackedLink";
import { track } from "@lib/track";
import BodyBallancerBookingClient from "./BodyBallancerBookingClient";
import BallancerPartnerForm from "./BallancerPartnerForm";
import {
  BALLANCER_PATHWAYS,
  BALLANCER_HUB_FAQS,
  BALLANCER_DISCLAIMER,
  BALLANCER_OUTCOME_ARC,
  BALLANCER_CATCHMENT,
} from "@/app/data/ballancerPathways";
import { LOCATION_LIST, locationDaysLabel, type BallancerLocationId } from "@lib/ballancerLocations";
import type { BlogCard } from "@lib/latestBlogs";

function OverlineLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-brand)] mb-4">
      {children}
    </p>
  );
}

function Section({
  id,
  className = "",
  children,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={`w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-20 ${className}`}>
      <div className="max-w-4xl mx-auto">{children}</div>
    </section>
  );
}

function WideSection({
  id,
  className = "",
  children,
}: {
  id?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className={`w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-20 ${className}`}>
      <div className="max-w-5xl mx-auto">{children}</div>
    </section>
  );
}

/**
 * The days a given site runs, read off the single rota in ballancerLocations.ts
 * rather than restated here, plus the fixed window for the hired rooms. If the
 * rota changes, this section changes with it.
 */
function daysFor(loc: (typeof LOCATION_LIST)[number]): string {
  const days = locationDaysLabel(loc.id);
  if (!loc.hours) return days;
  const fmt = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`;
  };
  const [start, end] = loc.hours.split("-");
  return `${days} · ${fmt(start)}–${fmt(end)}`;
}

/** The hub page that publishes a public site's full address. */
const ADDRESS_PAGE: Partial<Record<BallancerLocationId, { href: string; label: string }>> = {
  stockshouse: { href: "/about/wellbeing-southport", label: "Southport clinic page" },
  manchester: { href: "/about/wellbeing-manchester", label: "Manchester clinic page" },
};

function BookCTA({
  label,
  trackLabel,
  size = "lg",
  color = "base",
  position,
}: {
  label: string;
  /**
   * The analytics label, separate from the visible text. The hero CTA was the
   * only pro_balancer_cta on the page until 2026-09-08 and carries ~42 events of
   * history under the label "Book Your First Session"; a second button reusing
   * that label would silently double the series and make before/after on the
   * restructure unreadable. Same button text, distinct label — see the tracking
   * skill, "Event is not double-counted across code paths".
   */
  trackLabel?: string;
  size?: "md" | "lg";
  color?: "base" | "oat" | "duck" | "neutral";
  position: string;
}) {
  return (
    <TrackedLink
      href="#book"
      variant="solid"
      size={size}
      color={color}
      event="pro_balancer_cta"
      label={trackLabel ?? label}
      component="BodyBallancerClient"
      meta={{ position }}
      className="inline-flex items-center gap-2 font-semibold"
    >
      {label}
      <ChevronRight className="h-4 w-4" />
    </TrackedLink>
  );
}

export default function BodyBallancerClient({ partnerReading = [] }: { partnerReading?: BlogCard[] }) {
  return (
    <main className="flex flex-col items-center">

      {/* ── HERO ── */}
      <section className="w-full bg-[var(--color-bg-muted)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* Text */}
          <div>
            <OverlineLabel>Advanced Lymphatic Therapy</OverlineLabel>

            {/* The page had no h1 at all until 2026-09-08 — it opened on two
                horizontal rules, an overline and two stat boxes, so nothing ever
                stated what it was, for a reader or for a crawler. The geo terms
                are in the heading deliberately: sessions run at four sites across
                the North West and "Body Ballancer Preston/Southport/Manchester" is
                the search this page should own. Alderley Edge is one day a month,
                so it is named in the locations section rather than the h1. */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-gray-800 leading-tight">
              Body Ballancer Lymphatic Drainage in{" "}
              <em className="text-[var(--color-brand)] not-italic">Preston, Southport &amp; Manchester</em>
            </h1>

            <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-xl">
              Medical-grade compression therapy that moves what your body cannot move on its
              own — after surgery, after hard training, or when you are simply stuck.
            </p>

            {/* Price, duration and location on the first screen. All three used
                to be buried: the price only appeared in the booking widget at the
                very bottom of the page. */}
            <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-semibold uppercase tracking-widest text-gray-500">
              <span>45 Minutes</span>
              <span aria-hidden="true" className="text-[var(--color-brand)]/40">·</span>
              <span>From £45</span>
              <span aria-hidden="true" className="text-[var(--color-brand)]/40">·</span>
              <span>Four Sites</span>
            </div>

            <div className="mt-8 flex flex-col items-start gap-3">
              <BookCTA
                label="Book a session"
                trackLabel="Book Your First Session"
                position="hero"
              />
              {/* /my-sessions took 18 views over Jun–Sep 2026 with no link to it
                  from anywhere on this page. "Book Your First Session" was also
                  the only CTA, which reads as a closed door to anyone who has
                  already booked one. */}
              <TrackedLink
                href="/treatments/body-ballancer/my-sessions"
                variant="text"
                size="md"
                color="base"
                event="pro_balancer_returning_client"
                label="My Sessions"
                component="BodyBallancerClient"
                meta={{ position: "hero" }}
                className="inline-flex items-center gap-1 !px-0"
              >
                Already a client? See your sessions
                <ChevronRight className="h-4 w-4" />
              </TrackedLink>
            </div>

            {/* Named, not "clinically overseen" by nobody. This sits in the first
                200 words, which is the slice AI answer engines weight when
                deciding whether a health page is citable, and it is also the
                thing a client spending £350 on a block of 10 wants to know. */}
            <p className="mt-6 text-sm text-gray-500">
              With{" "}
              <Link
                href="/about/founder"
                className="font-medium text-gray-700 underline underline-offset-4 hover:text-[var(--color-brand)]"
              >
                Niki Charnock
              </Link>
              , Naturopathic Nutritionist · 25+ years in elite sport
            </p>

            {/* Special offer pill */}
            <Link
              href="/treatments/body-ballancer?offer=1#book"
              onClick={() => {
                try {
                  track(
                    "pro_balancer_offer_pill",
                    { label: "Express Mini Detox + 2 Sessions £130", component: "BodyBallancerClient" },
                    { event: "pro_balancer_offer_pill", label: "Express Mini Detox + 2 Sessions £130", component: "BodyBallancerClient" }
                  );
                } catch {}
              }}
              className="mt-8 inline-flex items-center gap-3 rounded-full border border-[var(--color-brand)]/30 bg-white pl-4 pr-5 py-2 shadow-sm hover:shadow-md transition group"
            >
              <span className="inline-block rounded-full bg-[var(--color-brand)] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">
                Offer
              </span>
              <span className="text-sm text-gray-700 leading-tight">
                <span className="font-semibold text-gray-900">Express Mini Detox + 2 Sessions</span>{" "}
                — <span className="font-semibold text-[var(--color-brand)]">£130</span>
                {/* Sessions run at several sites on a rota now, so the pack is handed over
                    wherever the client's first session falls — the confirmation email names it. */}
                <span className="block text-xs text-gray-400">Detox pack handed to you at your first session</span>
              </span>
              <ChevronRight className="h-4 w-4 text-[var(--color-brand)] group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              href="/shop/bundles/express-detox-pack"
              className="mt-2 inline-block text-xs text-gray-500 underline underline-offset-4 hover:text-[var(--color-brand)]"
            >
              What&apos;s in the Express Mini Detox? →
            </Link>
          </div>

          {/* Image */}
          <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[3/4] w-full max-w-sm mx-auto lg:max-w-none">
            <Image
              src="/images/body-ballancer.webp"
              alt="Advanced lymphatic therapy — Pro Balancer treatment"
              fill
              className="object-cover object-top"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-brand)]/30 to-transparent" />
          </div>

        </div>
      </section>

      {/* ── PRACTITIONER QUOTE ── */}
      <Section className="bg-white">
        <blockquote className="border border-gray-200 rounded-2xl p-8 sm:p-10">
          <p className="text-lg sm:text-xl text-gray-600 italic leading-relaxed mb-6">
            &ldquo;With over 25 years working at the highest levels of professional sport — from international rugby and football to elite swimmers and Ironman triathletes — I have witnessed first-hand how a compromised lymphatic system silently limits recovery, performance and long-term health.&rdquo;
          </p>
          {/* This was attributed to "Your Practitioner" — a first-person clinical
              claim on a health page with no name and no qualification behind it,
              which is precisely the pattern Google's quality guidance discounts.
              The credentials named here are ONLY the two the site actually
              displays (CNM London, ANP membership, with their logos on
              /about/founder). Do not add BANT or DipNT — see the note above
              personJsonLd in app/about/founder/page.tsx. */}
          <footer className="flex items-center gap-4">
            <Image
              src="/images/Niki_Charnock.webp"
              alt="Niki Charnock, Naturopathic Nutritionist at CF Organic Wellness"
              width={56}
              height={56}
              className="h-14 w-14 flex-shrink-0 rounded-full object-cover object-top"
            />
            <div>
              <cite className="block text-base font-semibold text-gray-800 not-italic">
                Niki Charnock
              </cite>
              <span className="block text-sm leading-relaxed text-gray-500">
                Naturopathic Nutritionist and founder of CF Organic Wellness. Trained at the
                College of Naturopathic Medicine (London) and a member of the Association of
                Naturopathic Practitioners (ANP), following 25+ years in elite sports
                rehabilitation.
              </span>
              <Link
                href="/about/founder"
                className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-brand)] hover:underline"
              >
                Read Niki&apos;s full background
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </footer>
        </blockquote>
      </Section>

      {/* ── FIVE PATHWAYS ── */}
      {/* Moved above the mechanism sections: "what is in this for me" now comes
          before "how the machine works". Each card is a link to its own page. */}
      <WideSection id="pathways" className="bg-[var(--color-bg-muted)]">
        <div className="text-center mb-14">
          <OverlineLabel>What We Treat</OverlineLabel>
          <h2 className="text-3xl sm:text-4xl font-light text-gray-800 mb-4">Five Pathways to a Better Body</h2>
          <p className="text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Whether your goal is faster recovery, injury prevention, improved aesthetics, surgical aftercare, or deep cellular detoxification — Pro Balancer therapy has a specific, evidence-informed role to play. Each pathway has a page of its own.
          </p>
        </div>
        <div className="space-y-4">
          {BALLANCER_PATHWAYS.map(({ id, num, symbol, name, cardBody }) => (
            <Link
              key={id}
              href={`/treatments/body-ballancer/${id}`}
              className="flex gap-6 items-start rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 transition hover:border-[var(--color-brand)] hover:shadow-sm group"
            >
              <div className="flex-shrink-0 w-10 h-10 border border-gray-200 rounded flex items-center justify-center text-gray-400">
                {symbol}
              </div>
              <div className="flex-1">
                <div className="flex items-baseline gap-4 mb-2">
                  <h3 className="text-lg font-light text-gray-800 group-hover:text-[var(--color-brand)]">{name}</h3>
                  <span className="text-3xl font-light text-[var(--color-brand)]/20 ml-auto">{num}</span>
                </div>
                <p className="text-gray-600 leading-relaxed">{cardBody}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-brand)]">
                  Read more
                  <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8 p-8 border border-gray-200 rounded-2xl bg-white">
          <div className="flex gap-4 items-start">
            <span className="text-2xl flex-shrink-0 text-gray-400">⬡</span>
            <div>
              <h3 className="font-light text-xl text-gray-500 mb-2">Whole Body Wellbeing</h3>
              <p className="text-gray-500 leading-relaxed">
                When your lymphatic system flows freely, everything changes — energy, mood, clarity, resilience. This is the foundation of lasting health, not a luxury add-on.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-10 text-center">
          <BookCTA
            label="Book a session"
            trackLabel="Book Your First Session - Pathways"
            position="pathways"
          />
        </div>
      </WideSection>

      {/* ── WHERE SESSIONS RUN ── */}
      {/* Four sites, 20–40 miles apart, on one rota. This is both a practical
          question a visitor has before booking and the page's claim on "Body
          Ballancer Preston" / "Southport" / "Manchester" / "Alderley Edge". Areas
          only — Nicola's street address is private (publicAddress: false) and all
          full addresses live in env vars read server-side, so none can reach the
          client bundle from here. */}
      <Section id="locations" className="bg-white">
        <OverlineLabel>Where Sessions Run</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          Four Sites, One Rota
        </h2>
        <p className="text-gray-600 leading-relaxed mb-10 max-w-2xl">
          Body Ballancer runs in Manchester at the Royal Exchange every Monday, at
          Nicola&apos;s clinic near Preston on Sundays, Tuesdays and Wednesdays, at Stocks
          House near Southport from Thursday to Saturday, and at Yoga on the Edge in Alderley Edge one
          Wednesday a month. You choose your site first when you book, and the calendar then
          shows only the days that site runs.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {LOCATION_LIST.map((loc) => (
            <div key={loc.id} className="border border-gray-200 rounded-2xl p-6 sm:p-8 bg-white">
              <h3 className="text-xl font-light text-gray-800 mb-1">{loc.name}</h3>
              <p className="text-sm text-gray-500 mb-4">{loc.area}</p>
              <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)] mb-2">
                {daysFor(loc)}
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                {loc.publicAddress && ADDRESS_PAGE[loc.id] ? (
                  <>
                    The full address is on our{" "}
                    <Link
                      href={ADDRESS_PAGE[loc.id]!.href}
                      className="text-[var(--color-brand)] underline underline-offset-4"
                    >
                      {ADDRESS_PAGE[loc.id]!.label}
                    </Link>
                    , and comes with your confirmation email.
                  </>
                ) : loc.publicAddress ? (
                  <>The full address comes with your confirmation email.</>
                ) : (
                  <>
                    A private clinic, so the full address comes with your confirmation email
                    rather than being listed publicly.
                  </>
                )}
              </p>
              {BALLANCER_CATCHMENT[loc.id]?.length ? (
                <p className="mt-4 text-xs text-gray-400 leading-relaxed">
                  Within easy reach of {BALLANCER_CATCHMENT[loc.id].slice(0, -1).join(", ")} and{" "}
                  {BALLANCER_CATCHMENT[loc.id].slice(-1)}.
                </p>
              ) : null}
              {loc.arrivalNotes ? (
                <p className="mt-3 text-xs text-gray-400 leading-relaxed">{loc.arrivalNotes}</p>
              ) : null}
            </div>
          ))}
        </div>
      </Section>

      {/* ── THE FOUNDATION ── */}
      <Section className="bg-white">
        <OverlineLabel>The Foundation</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          Why Your Lymphatic System is the{" "}
          <em className="text-[var(--color-brand)]">Key to Everything</em>
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-10">
          <p>
            Your lymphatic system is the body&apos;s internal plumbing.{" "}
            <strong>Unlike your blood, which has the heart as its pump, your lymph has no central pump at all.</strong>{" "}
            It depends entirely on movement, breath, muscle contraction — and when life slows you down through injury, surgery, stress, or a sedentary lifestyle, your lymph slows with it.
          </p>
          <p>
            When lymph becomes sluggish, the consequences are felt throughout the entire body: a build-up of metabolic waste, excess fluid in the tissues, sluggish detoxification, chronic low-grade inflammation, that feeling of heaviness and bloating you cannot shake, and — over time — a very real contribution to chronic disease.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-[var(--color-brand)]/20 p-6 rounded-2xl text-center">
            <div className="text-3xl font-light text-[var(--color-brand)] mb-2">5</div>
            <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">Key Treatment Pathways</div>
          </div>
          <div className="border border-[var(--color-brand)]/20 p-6 rounded-2xl text-center">
            <div className="text-3xl font-light text-[var(--color-brand)] mb-2">360°</div>
            <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">Whole Body Approach</div>
          </div>
        </div>
      </Section>

      {/* ── THE TECHNOLOGY ── */}
      <Section className="bg-[var(--color-bg-muted)]">
        <OverlineLabel>The Technology</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-6">
          Medical Grade.<br />
          Clinically Evidenced.<br />
          Personally Delivered.
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-10">
          <p>
            The Pro Balancer Lymphatic System uses precision pneumatic compression therapy to externally mimic what healthy movement does naturally:{" "}
            <strong>gently and rhythmically moving lymph fluid through the body&apos;s vessels, stimulating drainage, clearing congestion, and opening the pathways your cells depend on to stay clean and functional.</strong>
          </p>
          <p>
            This is not simply a massage. This is a targeted, medically-informed protocol — designed and delivered by a practitioner with over two decades of elite sport and functional wellness experience — to transform the way your body heals, recovers, and functions at every level.
          </p>
        </div>
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-2xl overflow-hidden">
          {[
            {
              symbol: "⟁",
              heading: "Pneumatic Compression",
              body: "Precisely calibrated air pressure sequences work systematically through the lymphatic chain, replicating the rhythm of healthy manual drainage at a clinical level.",
            },
            {
              symbol: "◎",
              heading: "Myofascial Release Integration",
              body: "Combined with hands-on myofascial release techniques, each session works to release fascial restrictions that accumulate through training, posture and daily life.",
            },
            {
              symbol: "✦",
              heading: "Cellulite Reduction & Skin Smoothing",
              body: "By clearing stagnant lymph and reducing fluid congestion in subcutaneous tissue, consistent treatment visibly improves skin texture and reduces the appearance of cellulite.",
            },
            {
              symbol: "▲",
              heading: "Post-Operative Risk Reduction",
              body: "This medical-grade system is used in clinical protocols post-operatively to reduce the risk of cellulitis, lymphoedema and post-surgical fibrosis.",
            },
          ].map(({ symbol, heading, body }) => (
            <div key={heading} className="flex gap-5 p-6 bg-white">
              <div className="flex-shrink-0 w-10 h-10 border border-gray-200 rounded flex items-center justify-center text-gray-400">
                {symbol}
              </div>
              <div>
                <h3 className="font-semibold text-[var(--color-brand)] text-xs uppercase tracking-widest mb-2">{heading}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── PRACTITIONER BACKGROUND ── */}
      <Section className="bg-white">
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
          <p>
            Every session is built around <strong>your specific needs</strong> — whether you are recovering from surgery, managing the demands of heavy training, supporting a liver detox protocol, or simply committed to maintaining your long-term health and resilience.
          </p>
          <p>
            Having spent over 25 years working with professional athletes across rugby, football, swimming and Ironman triathlon — treating injuries, guiding rehab, and developing performance programmes at the highest level — I bring a depth of anatomical understanding, clinical insight, and functional nutrition expertise to every session.
          </p>
          <p>
            In recent years, my work has deepened into the functional medicine space, looking beyond the musculoskeletal system to address{" "}
            <strong>gut health, liver detoxification, systemic inflammation, and the gut–brain axis</strong> — because lasting health and a pain-free life depend on all of these systems working in concert.
          </p>
          <p>
            <strong>The lymphatic system sits at the crossroads of all of them.</strong> It is the single most commonly overlooked variable in both recovery and long-term wellness — and this machine gives us the ability to address it with genuine clinical precision.
          </p>
        </div>
      </Section>

      {/* ── THE OUTCOMES ARC ── */}
      {/* Was two columns: "You'll Feel It Quickly" / "The Long Game". The second
          column answered a different question from the one people ask — it
          replied to "what will I feel like after ten sessions?" with cellular
          chemistry. Now three stages, and every physiological claim survives as
          the `why` under its felt claim. Content in app/data/ballancerPathways.ts. */}
      <Section id="outcomes" className="bg-[var(--color-bg-muted)]">
        <div className="mb-12">
          <OverlineLabel>What To Expect</OverlineLabel>
          <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-4">
            One Session, and a Course
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed max-w-2xl">
            A single session is worth having on its own. A course is what makes the change hold.
            Here is what that actually feels like at each stage.
          </p>
        </div>

        <ol className="space-y-6">
          {BALLANCER_OUTCOME_ARC.map((stage, i) => (
            <li
              key={stage.key}
              className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8"
            >
              <div className="flex items-baseline gap-4 mb-1">
                <span className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)]">
                  {stage.eyebrow}
                </span>
                <span aria-hidden="true" className="ml-auto text-3xl font-light text-[var(--color-brand)]/20">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl font-light text-gray-800 mb-3">{stage.heading}</h3>
              <p className="text-gray-600 leading-relaxed mb-6">{stage.intro}</p>

              <ul className="space-y-4">
                {stage.items.map((item) => (
                  <li key={item.felt} className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-brand)]/50"
                    />
                    <div>
                      <p className="text-gray-700 leading-relaxed">{item.felt}</p>
                      {item.why ? (
                        <p className="mt-1 text-sm text-gray-500 leading-relaxed">{item.why}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>

              {stage.note ? (
                <p className="mt-6 border-l-2 border-[var(--color-brand)]/30 pl-5 text-sm text-gray-500 leading-relaxed">
                  {stage.note}
                </p>
              ) : null}
            </li>
          ))}
        </ol>

        <div className="mt-10 text-center">
          <BookCTA
            label="Book a session"
            trackLabel="Book Your First Session - Outcomes"
            position="outcomes"
          />
        </div>
      </Section>

      {/* ── IMAGE BREAK ── */}
      <div className="w-full relative overflow-hidden" style={{ height: "40vh", minHeight: "240px", maxHeight: "480px" }}>
        <Image
          src="/images/body-ballancer.webp"
          alt="Pro Balancer lymphatic therapy session"
          fill
          className="object-cover object-center"
        />
        <div className="absolute inset-0 bg-[var(--color-brand)]/20" />
      </div>

      {/* ── THE BIGGER PICTURE ── */}
      <Section id="bigger-picture" className="bg-white">
        <OverlineLabel>The Bigger Picture</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          Lymphatic Health <em className="text-[var(--color-brand)]">Is</em> Longevity
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-8">
          <p>
            We live in a world that places an ever-growing burden on our bodies. Environmental toxins, ultra-processed foods, psychological stress, electromagnetic exposure, pharmaceutical residues, and the relentless pace of modern life create a toxic load that our ancestors simply never faced. We cannot eliminate these external pressures. We can, however, become far more deliberate about how effectively we remove them from our system.
          </p>
        </div>
        <blockquote className="border border-gray-200 rounded-2xl p-8 mb-8">
          <p className="text-lg text-gray-500 italic leading-relaxed text-center">
            &ldquo;The question is not whether your body is being exposed to toxins and inflammatory burden — in the modern world, it inevitably is. The question is how efficiently your vessel is equipped to move them out.&rdquo;
          </p>
        </blockquote>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4">
          <p>
            In functional medicine, we talk about the concept of the drainage funnel: colon first, then gut, then lymph, then liver — each layer must be open before the one above can offload effectively. Chronic fatigue, persistent inflammation, hormonal imbalance, skin conditions, joint pain, brain fog — these are often not isolated symptoms. They are signals from a system that is backed up, overwhelmed, and struggling to keep the drainage flowing.
          </p>
          <p>
            Regular Pro Balancer lymphatic drainage therapy is one of the most direct, evidence-supported interventions available to keep that system open — and to give your body the best possible conditions to heal, regenerate, and thrive.
          </p>
        </div>
      </Section>

      {/* ── FAQ ── */}
      {/* Rendered visibly, and the FAQPage JSON-LD in page.tsx reads this same
          list. Google forbids the markup for answers a visitor cannot see and
          can disqualify the page from rich results entirely — so if this section
          ever gets collapsed away, the schema has to go with it. */}
      <Section id="faq" className="bg-[var(--color-bg-muted)]">
        <OverlineLabel>Common Questions</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-8">
          Body Ballancer — Your Questions
        </h2>
        <div className="divide-y divide-gray-200 border-y border-gray-200">
          {BALLANCER_HUB_FAQS.map((f) => (
            <details key={f.q} className="group py-4">
              <summary className="cursor-pointer list-none text-lg font-medium text-[var(--color-brand)] marker:content-none">
                <h3 className="inline">{f.q}</h3>
              </summary>
              <p className="mt-3 text-base leading-7 text-gray-600">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* ── BOOK ── */}
      <Section id="book" className="bg-white">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-brand)] mb-4">Book Online</p>
          <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-4">
            Your Health Cannot Wait.<br />Neither Should You.
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl leading-relaxed">
            Whether you are preparing for surgery, recovering from one, managing the demands of training, or simply committed to understanding and supporting your body at a deeper level — there is a session designed for you.
          </p>
        </div>
        <BodyBallancerBookingClient />
        <p className="mt-6 text-xs text-gray-400 leading-relaxed">{BALLANCER_DISCLAIMER}</p>
      </Section>

      {/* ── REFERRAL PARTNERS ── */}
      {/* Below the booking flow on purpose: this is a client page, and a client
          should never have to scroll past a B2B pitch to reach the booker. The
          #partners anchor is the link Nicola sends to practitioners, so they
          land straight on their own section rather than a sales page aimed at
          someone else. The form requests a free session; it never books one. */}
      <Section id="partners" className="bg-[var(--color-bg-muted)]">
        <OverlineLabel>For Practitioners &amp; Businesses</OverlineLabel>
        <h2 className="text-3xl sm:text-4xl font-light text-gray-800 leading-tight mb-6">
          Refer With Confidence.<br />
          <em className="text-[var(--color-brand)]">Try It Yourself First.</em>
        </h2>
        <div className="prose prose-lg max-w-none text-gray-600 space-y-4 mb-10">
          <p>
            If you work with clients recovering from surgery, training hard, or looking for support alongside aesthetic treatments — physiotherapists, sports therapists, personal trainers, clinics and salons — we would love to work with you.
          </p>
          <p>
            You shouldn&apos;t recommend something you haven&apos;t felt. Leave your details and Nicola will arrange a <strong>complimentary Body Ballancer session</strong> for you, then talk through how referrals could work for your clients.
          </p>
        </div>
        {/* A partner arriving on the #partners link Nicola sends lands here
            first, having skipped everything above. "#top" scrolls to the top of
            the document (HTML spec; Next's Link handles it the same way), so
            there's no id to add to the hero. Its own event, not
            pro_balancer_cta: this is a referrer reading up, not client intent. */}
        <TrackedLink
          href="#top"
          variant="text"
          size="md"
          color="base"
          event="ballancer_partner_read_more"
          label="Read More"
          component="BodyBallancerClient"
          meta={{ position: "partners" }}
          className="-mt-4 mb-8 inline-flex items-center gap-1 !px-0"
        >
          Want to read more about the treatment first? Start at the top
          <ChevronRight className="h-4 w-4 -rotate-90" />
        </TrackedLink>
        <BallancerPartnerForm />
        {/* Below the form so the ask comes first. Image cards with titles only:
            the section already carries a block of copy, and a list of titles
            read as more of it. */}
        {partnerReading.length ? (
          <div className="mt-12">
            <h3 className="text-2xl font-light text-gray-800 mb-6">
              Articles for you and your clients
            </h3>
            <ul className="grid gap-5 sm:grid-cols-2">
              {partnerReading.map((b) => (
                <li
                  key={b.slug}
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:border-[var(--color-brand)]/40"
                >
                  <TrackedLink
                    href={`/blog/${b.slug}`}
                    variant="text"
                    size="md"
                    color="base"
                    event="ballancer_partner_read_more"
                    label={`Blog - ${b.slug}`}
                    component="BodyBallancerClient"
                    meta={{ position: "partners" }}
                    className="!p-0 flex h-full flex-col text-left"
                  >
                    {b.image ? (
                      <div className="relative aspect-[16/9] w-full bg-[var(--color-bg-muted)]">
                        <Image
                          src={b.image}
                          alt=""
                          fill
                          loading="lazy"
                          sizes="(min-width: 640px) 440px, 100vw"
                          className="object-cover"
                        />
                      </div>
                    ) : null}
                    <span className="flex items-start gap-2 p-4 font-medium text-[var(--color-brand)]">
                      <span className="flex-1">{b.title}</span>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0" />
                    </span>
                  </TrackedLink>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Section>

    </main>
  );
}
